import { computed, Ref } from '@vue/composition-api';
import { SelectBoxChoice } from '@front-app/components/inputs/SelectBoxModel';
import { EventV2 } from '@front-app/services/EventsApi/EventModel';
import { closestTo, min } from 'date-fns';

const getKey = (d: Date): string => `${d.getFullYear()}-${d.getMonth()}`;

export interface EventDateChoice extends SelectBoxChoice {
	date: Date;
}

export interface GoToEventDates {
	todayKey: string;
	options: Ref<EventDateChoice[]>;
	getDateByKey: (key: string) => Date;
	getClosestNonTodayDate: () => Date;
}

/**
 * Aggregates a list of events into a set of month-year pairs. At minimum always
 * includes the current month and year.
 *
 * @param events A  Ref of a list of events to aggregate
 */
export function useGoToEventDates(events: Ref<EventV2[]>, now = new Date()): GoToEventDates {
	const todayKey = getKey(now);
	const keysToDates = computed(() => events.value.reduce((acc: Record<string, Date>, curr: EventV2) => {
		const key = getKey(curr.startDate);
		const existing = acc[key] || curr.startDate;
		acc[key] = min([existing, curr.startDate]);
		return acc;
	}, { [todayKey]: now }));
	const options = computed((): EventDateChoice[] => Object
		.entries(keysToDates.value)
		.sort(([, a], [, b]) => a.getTime() - b.getTime())
		.map(([key]) => ({
			label: key,
			key,
			date: keysToDates.value[key],
		})));
	const getDateByKey = (key: string) => keysToDates.value[key];
	const getClosestNonTodayDate = (): Date => {
		const vals = Object.values(keysToDates.value)
			.filter((val) => val !== getDateByKey(todayKey));
		return closestTo(new Date(), vals);
	};
	return {
		todayKey,
		options,
		getDateByKey,
		getClosestNonTodayDate,
	};
}
